/**
 * Timeline, atividades e tarefas (DEMO-03, telas/Atividades.tsx), persistentes — três coisas DIFERENTES:
 *   • Evento automático: registrado pelo sistema, sem intervenção humana (histórico).
 *   • Atividade manual: algo que a pessoa FEZ (ligação, e-mail, reunião, WhatsApp, visita…).
 *   • Tarefa futura: compromisso a CUMPRIR (tem responsável e data).
 * "Registrar atividade" pode gerar a próxima tarefa, mas atividade nunca vira tarefa sozinha.
 * A conclusão da tarefa usa data e hora EFETIVAS do servidor.
 */
import { CamposConclusao, conclusaoInicial, corpoConclusao, faltasConclusao, ModalConcluirProximaAcao, type Conclusao } from './ProximaAcao.tsx';
import { usePersonalizacao } from './personalizacao.tsx';
import { useState, type ReactNode } from 'react';
import { api } from '../api.ts';
import { navegar } from '../rotas.ts';
import { useSessao } from '../sessao.tsx';
import { dataBr, estagioAberto, type SituacaoTarefa } from '../../../backend/src/compartilhado/oportunidades.ts';
import { Ajuda, Estado, EstadoCarga, Modal, ResumoIndicadores, useCarga } from './ui.tsx';
import { CampoData, CampoDataHora } from './CampoData.tsx';
import type { Cliente } from './tipos.ts';
import type { OpcoesOportunidade, OportunidadeResumo } from './tiposOportunidade.ts';

/** "Agora" no fuso da INTTRIX, no formato do campo de data e hora. */
const agoraSaoPaulo = () => new Date().toLocaleString('sv-SE', { timeZone: 'America/Sao_Paulo' }).slice(0, 16).replace(' ', 'T');

export interface ItemTarefa {
  chave: string;
  id?: string;
  titulo: string;
  responsavel: { id: string; nome: string };
  data: string;
  concluida: boolean;
  cliente: { id: string; nome: string };
  oportunidade?: { id: string; numero: string };
  origem: 'Tarefa' | 'Próxima ação';
  modulo: string;
  detalhe?: string;
  situacao: 'vencidas' | 'hoje' | 'proximas' | 'concluidas';
}
interface ItemLinha {
  chave: string;
  tipo: 'evento' | 'atividade';
  data: string;
  hora?: string;
  titulo: string;
  detalhe?: string;
  autor: string;
  oportunidade?: { id: string; numero: string };
  contato?: string;
  tarefa?: string;
}

/* =====================================================================
 * Registrar atividade (manual) — com opção de gerar a próxima tarefa
 * ===================================================================== */
export function ModalRegistrarAtividade({ clienteId, oportunidadeId, contatoId: contatoInicial, aoFechar, aoSalvar }: { clienteId: string; oportunidadeId?: string; contatoId?: string; aoFechar: () => void; aoSalvar: () => void }) {
  const carga = useCarga(() => Promise.all([api.get<Cliente>(`/api/clientes/${clienteId}`), api.get<OportunidadeResumo[]>(`/api/oportunidades?cliente_id=${clienteId}`), api.get<OpcoesOportunidade>('/api/oportunidades/opcoes')]), [clienteId]);
  if (!carga.dados)
    return (
      <Modal titulo="Registrar atividade" aoFechar={aoFechar} rodape={null}>
        <EstadoCarga carregando={carga.carregando} erro={carga.erro} recarregar={carga.recarregar} />
      </Modal>
    );
  const [c, ops, opcoes] = carga.dados;
  return <FormAtividade c={c} ops={ops} opcoes={opcoes} oportunidadeId={oportunidadeId} contatoInicial={contatoInicial} aoFechar={aoFechar} aoSalvar={aoSalvar} />;
}

function FormAtividade({ c, ops, opcoes, oportunidadeId, contatoInicial, aoFechar, aoSalvar }: { c: Cliente; ops: OportunidadeResumo[]; opcoes: OpcoesOportunidade; oportunidadeId?: string; contatoInicial?: string; aoFechar: () => void; aoSalvar: () => void }) {
  const hoje = opcoes.hoje;
  const [tipo, setTipo] = useState(opcoes.tipos_atividade[0] ?? '');
  const [dataHora, setDataHora] = useState(agoraSaoPaulo());
  const [contatoId, setContatoId] = useState(contatoInicial ?? c.contatos.find((k) => k.principal && k.status === 'ativo')?.id ?? '');
  const [op, setOp] = useState(oportunidadeId ?? '');
  const [resumo, setResumo] = useState('');
  const [resultado, setResultado] = useState('');
  const [observacao, setObservacao] = useState('');
  const [gerar, setGerar] = useState(false);
  const [tarefa, setTarefa] = useState({ titulo: '', responsavel_id: ops.find((o) => o.id === oportunidadeId)?.responsavel.id ?? c.responsavel_id, data: '' });
  // P3-12: opção explícita (desmarcada) de concluir também a próxima ação da Oportunidade relacionada
  const opSel = ops.find((o) => o.id === op);
  const [concluirAcao, setConcluirAcao] = useState(false);
  const [conclusao, setConclusao] = useState<Conclusao>(conclusaoInicial(opSel?.proxima_acao?.responsavel.id ?? c.responsavel_id));
  const [tentou, setTentou] = useState(false);
  const [erro, setErro] = useState('');
  const [salvando, setSalvando] = useState(false);
  const faltam = [...(concluirAcao && opSel?.proxima_acao ? faltasConclusao(conclusao) : []), !tipo && 'Selecione o tipo da atividade.', !resumo.trim() && 'Informe o resumo da atividade.', !dataHora && 'Informe a data e a hora.', gerar && !tarefa.titulo.trim() && 'Informe o título da próxima tarefa.', gerar && !tarefa.data && 'Informe a data da próxima tarefa.', gerar && tarefa.data && tarefa.data < hoje && 'A próxima tarefa precisa ter data de hoje em diante.'].filter(Boolean) as string[];
  const salvar = async () => {
    setTentou(true);
    if (faltam.length || salvando) return;
    setErro('');
    setSalvando(true);
    try {
      await api.post('/api/atividades', {
        cliente_id: c.id,
        tipo,
        data_hora: dataHora,
        contato_id: contatoId || undefined,
        oportunidade_id: op || undefined,
        resumo: resumo.trim(),
        resultado: resultado.trim(),
        observacao: observacao.trim(),
        proxima_tarefa: gerar ? { titulo: tarefa.titulo.trim(), responsavel_id: tarefa.responsavel_id, data: tarefa.data } : undefined,
        concluir_proxima_acao: concluirAcao && opSel?.proxima_acao ? corpoConclusao(conclusao) : undefined,
      });
      aoSalvar();
    } catch (e) {
      setErro((e as Error).message);
    } finally {
      setSalvando(false);
    }
  };
  return (
    <Modal
      titulo={`Registrar atividade — ${c.nome}`}
      aoFechar={aoFechar}
      largo
      rodape={
        <>
          <button type="button" className="botao-secundario" onClick={aoFechar}>
            Cancelar
          </button>
          <button type="button" className="botao-primario" onClick={salvar} disabled={salvando} data-teste="salvar-atividade">
            Registrar atividade
          </button>
        </>
      }
    >
      <p className="texto-secundario">Atividade = algo que você já fez. Se houver um próximo passo, gere a próxima tarefa (compromisso futuro). Eventos automáticos são registrados pelo sistema.</p>
      {erro && (
        <p className="aviso aviso-erro" role="alert" data-teste="erro-atividade">
          {erro}
        </p>
      )}
      {opcoes.tipos_atividade.length === 0 && <Estado tipo="bloqueado" titulo="a lista de tipos de atividade não está configurada neste ambiente" />}
      <div className="grade-form sem-borda form-atividade" data-teste="form-atividade">
        <label>
          Tipo
          <select value={tipo} onChange={(e) => setTipo(e.target.value)} data-teste="tipo-atividade">
            {opcoes.tipos_atividade.map((x) => (
              <option key={x}>{x}</option>
            ))}
          </select>
        </label>
        <CampoDataHora rotulo="Quando" valor={dataHora} aoMudar={setDataHora} teste="data-atividade" obrigatorio />
        <label>
          Contato relacionado
          <select value={contatoId} onChange={(e) => setContatoId(e.target.value)} data-teste="contato-atividade">
            <option value="">— nenhum —</option>
            {c.contatos.map((k) => (
              <option key={k.id} value={k.id}>
                {k.nome}
                {k.cargo ? ` (${k.cargo})` : ''}
                {k.status === 'inativo' ? ' — inativo' : ''}
              </option>
            ))}
          </select>
        </label>
        <label className="coluna-dupla">
          Oportunidade relacionada
          <select value={op} onChange={(e) => setOp(e.target.value)} disabled={Boolean(oportunidadeId)} data-teste="oportunidade-atividade">
            <option value="">— não se aplica —</option>
            {ops.map((o) => (
              <option key={o.id} value={o.id}>
                #{o.numero} — {o.titulo}
                {estagioAberto(o.estagio) ? '' : ' (encerrada)'}
              </option>
            ))}
          </select>
        </label>
        <label>
          Observação
          <input value={observacao} onChange={(e) => setObservacao(e.target.value)} />
        </label>
        <label className="largura-total">
          Resumo *
          <input value={resumo} onChange={(e) => setResumo(e.target.value)} placeholder="ex.: Ligação para alinhar o prazo de entrega" data-teste="resumo-atividade" />
        </label>
        <label className="largura-total">
          Resultado
          <input value={resultado} onChange={(e) => setResultado(e.target.value)} placeholder="ex.: Cliente pediu nova versão" data-teste="resultado-atividade" />
        </label>
        {opSel?.proxima_acao && estagioAberto(opSel.estagio) && (
          <div className="largura-total caixa-conclusao-atividade" data-teste="caixa-concluir-acao">
            <label className="opcao">
              <input type="checkbox" checked={concluirAcao} onChange={(e) => setConcluirAcao(e.target.checked)} data-teste="concluir-acao-atividade" />
              <span>
                Concluir também a próxima ação “{opSel.proxima_acao.descricao}”
                <Ajuda titulo="Concluir a próxima ação">Desmarcada por padrão: registrar uma atividade não conclui a próxima ação sozinho. Ao marcar, escolha a nova próxima ação ou “Sem próxima ação” com justificativa.</Ajuda>
              </span>
            </label>
            {concluirAcao && <CamposConclusao v={conclusao} setV={setConclusao} responsaveis={opcoes.responsaveis} teste="conclusao-atividade" />}
          </div>
        )}
        <label className="opcao largura-total caixa-proxima-tarefa">
          <input type="checkbox" checked={gerar} onChange={(e) => setGerar(e.target.checked)} data-teste="gerar-tarefa" />
          <span>Gerar próxima tarefa a partir desta atividade</span>
        </label>
        {gerar && (
          <div className="largura-total sub-tarefa" data-teste="campos-proxima-tarefa">
            <label>
              Próxima tarefa *
              <input value={tarefa.titulo} onChange={(e) => setTarefa({ ...tarefa, titulo: e.target.value })} data-teste="titulo-tarefa" />
            </label>
            <label>
              Responsável
              <select value={tarefa.responsavel_id} onChange={(e) => setTarefa({ ...tarefa, responsavel_id: e.target.value })} data-teste="responsavel-tarefa">
                {opcoes.responsaveis.map((r) => (
                  <option key={r.id} value={r.id}>
                    {r.nome}
                  </option>
                ))}
              </select>
            </label>
            <CampoData rotulo="Data da tarefa" valor={tarefa.data} aoMudar={(x) => setTarefa({ ...tarefa, data: x })} min={hoje} teste="data-tarefa" obrigatorio />
          </div>
        )}
      </div>
      {tentou && faltam.length > 0 && <Estado tipo="bloqueado" titulo="Falta completar" itens={faltam} teste="faltam-atividade" />}
    </Modal>
  );
}

const quando = (t: { data: string; situacao: ItemTarefa['situacao'] }) => (t.situacao === 'vencidas' ? `venceu em ${dataBr(t.data)}` : t.situacao === 'hoje' ? 'hoje' : dataBr(t.data));

/* =====================================================================
 * Timeline do Cliente
 * ===================================================================== */
export function TimelineCliente({ clienteId, aoMudar }: { clienteId: string; aoMudar?: () => void }) {
  const { pode } = useSessao();
  const [registrar, setRegistrar] = useState(false);
  const [filtro, setFiltro] = useState<'todos' | 'atividade' | 'evento'>('todos');
  const [erro, setErro] = useState('');
  const carga = useCarga(() => api.get<{ hoje: string; itens: ItemLinha[]; tarefas: ItemTarefa[]; referencias: Referencias }>(`/api/clientes/${clienteId}/timeline`), [clienteId]);
  if (!carga.dados) return <EstadoCarga carregando={carga.carregando} erro={carga.erro} recarregar={carga.recarregar} teste="timeline" />;
  const { itens, tarefas } = carga.dados;
  const linha = itens.filter((x) => filtro === 'todos' || x.tipo === filtro);
  const recarregar = () => (void carga.recarregar(), aoMudar?.());
  const concluir = async (id: string) => {
    setErro('');
    try {
      await api.post(`/api/tarefas/${id}/concluir`, {});
      recarregar();
    } catch (e) {
      setErro((e as Error).message);
    }
  };
  return (
    <section data-teste="timeline-cliente-secao">
      <div className="barra-titulo">
        <h2>Timeline</h2>
        {pode('criar', 'atividade') && (
          <div className="acoes-direita">
            <button type="button" className="botao-primario" onClick={() => setRegistrar(true)} data-teste="registrar-atividade">
              Registrar atividade
            </button>
          </div>
        )}
      </div>
      <p className="legenda-timeline texto-secundario">
        <span className="marca-linha marca-tarefa">◷ Tarefa futura</span> compromisso a cumprir · <span className="marca-linha marca-atividade">✎ Atividade manual</span> algo que alguém fez · <span className="marca-linha marca-evento">⚙ Evento automático</span> registrado pelo sistema
      </p>
      {erro && (
        <p className="aviso aviso-erro" role="alert">
          {erro}
        </p>
      )}
      <h3>Tarefas futuras ({tarefas.length})</h3>
      {tarefas.length ? (
        <ul className="lista-tarefas" data-teste="tarefas-cliente">
          {tarefas.map((t) => (
            <li key={t.chave} className={`item-linha item-tarefa situacao-${t.situacao}`}>
              <span className="marca-linha marca-tarefa">◷ Tarefa</span>
              <span>
                <strong>{t.titulo}</strong>{' '}
                <span className="texto-secundario">
                  · {t.responsavel.nome} · {quando(t)}
                  {t.oportunidade ? ` · #${t.oportunidade.numero}` : ''}
                  {t.origem === 'Próxima ação' ? ' · próxima ação' : ''}
                </span>
              </span>
              {t.id && pode('concluir', 'tarefa') && (
                <button type="button" className="botao-secundario pequeno" onClick={() => void concluir(t.id!)} data-teste={`concluir-${t.chave}`}>
                  Concluir
                </button>
              )}
            </li>
          ))}
        </ul>
      ) : (
        <Estado tipo="vazio" titulo="nenhuma tarefa futura para este Cliente" />
      )}
      <div className="barra-titulo">
        <h3>Histórico</h3>
        <div className="acoes-direita" role="group" aria-label="Filtrar o histórico">
          {(['todos', 'atividade', 'evento'] as const).map((f) => (
            <button key={f} type="button" className={`chip${filtro === f ? ' ativo' : ''}`} aria-pressed={filtro === f} onClick={() => setFiltro(f)} data-teste={`filtro-timeline-${f}`}>
              {f === 'todos' ? 'Tudo' : f === 'atividade' ? 'Atividades manuais' : 'Eventos automáticos'}
            </button>
          ))}
        </div>
      </div>
      {linha.length ? (
        <ul className="linha-tempo timeline-cliente" data-teste="timeline-cliente">
          {linha.map((t) => (
            <li key={t.chave} className={`item-linha item-${t.tipo}`} data-tipo={t.tipo} data-tarefa={t.tarefa}>
              <strong>
                {dataBr(t.data)}
                {t.hora ? ` ${t.hora}` : ''}
              </strong>
              <span>
                <span className={`marca-linha marca-${t.tipo}`}>{t.tipo === 'evento' ? '⚙ Evento automático' : '✎ Atividade manual'}</span> <TextoComReferencias texto={t.titulo} refs={carga.dados?.referencias} />
                {t.contato && <span className="texto-secundario"> · com {t.contato}</span>}
                {t.detalhe && <span className="texto-secundario"> · {t.detalhe}</span>}{' '}
                <span className="texto-secundario">
                  (
                  {t.oportunidade && (
                    <>
                      <a href={`#/oportunidades/${t.oportunidade.id}`} className="ref-timeline">
                        #{t.oportunidade.numero}
                      </a>
                      ,{' '}
                    </>
                  )}
                  {t.autor})
                </span>
              </span>
            </li>
          ))}
        </ul>
      ) : (
        <Estado tipo="vazio" titulo="nenhum registro com este filtro" />
      )}
      {registrar && <ModalRegistrarAtividade clienteId={clienteId} aoFechar={() => setRegistrar(false)} aoSalvar={() => (setRegistrar(false), recarregar())} />}
    </section>
  );
}

/* =====================================================================
 * Área de Tarefas (aberta já filtrada pelo Início)
 * ===================================================================== */
const ROTULOS_SITUACAO: Record<SituacaoTarefa, string> = { vencidas: 'Vencidas', hoje: 'Vencem hoje', proximas: 'Próximas', concluidas: 'Concluídas', abertas: 'Todas em aberto', todas: 'Todas' };
export function Tarefas({ consulta }: { consulta: URLSearchParams }) {
  const { pode } = useSessao();
  const situacao = (consulta.get('situacao') as SituacaoTarefa) || 'abertas';
  const responsavel = consulta.get('responsavel_id') ?? '';
  const [erro, setErro] = useState('');
  const [concluirAcao, setConcluirAcao] = useState<ItemTarefa | null>(null);
  const carga = useCarga(() => api.get<{ hoje: string; tarefas: ItemTarefa[]; contagem: Record<SituacaoTarefa, number>; responsaveis: { id: string; nome: string }[] }>(`/api/tarefas?situacao=${situacao}${responsavel ? `&responsavel_id=${responsavel}` : ''}`), [situacao, responsavel]);
  const irPara = (s: SituacaoTarefa, r = responsavel) => navegar(`/tarefas?situacao=${s}${r ? `&responsavel_id=${r}` : ''}`);
  // P2-03/P2-04 (D7): colunas e visões (filtros = situação e responsável)
  const pers = usePersonalizacao('tarefas', { filtros: { situacao, responsavel }, aplicarFiltros: (e: Record<string, unknown>) => irPara(((e.situacao as SituacaoTarefa) || 'abertas'), (e.responsavel as string) ?? '') });
  if (!carga.dados) return <EstadoCarga carregando={carga.carregando} erro={carga.erro} recarregar={carga.recarregar} teste="tarefas" />;
  const { tarefas: lista, contagem, responsaveis } = carga.dados;
  const nomeResponsavel = responsaveis.find((r) => r.id === responsavel)?.nome;
  const concluir = async (id: string) => {
    setErro('');
    try {
      await api.post(`/api/tarefas/${id}/concluir`, {});
      void carga.recarregar();
    } catch (e) {
      setErro((e as Error).message);
    }
  };
  return (
    <>
      <section className="resumo-destaque" data-teste="resumo-tarefas">
        <div className="cabecalho-registro">
          <h1 className="titulo-pagina">Tarefas</h1>
        </div>
        <ResumoIndicadores itens={(['vencidas', 'hoje', 'proximas', 'concluidas'] as const).map((s) => ({ rotulo: ROTULOS_SITUACAO[s], valor: contagem[s] }))} />
      </section>
      <div className="filtros">
        <label>
          Status
          <select value={situacao} onChange={(e) => irPara(e.target.value as SituacaoTarefa)} data-teste="filtro-situacao-tarefa">
            {(Object.keys(ROTULOS_SITUACAO) as SituacaoTarefa[]).map((s) => (
              <option key={s} value={s}>
                {ROTULOS_SITUACAO[s]} ({contagem[s]})
              </option>
            ))}
          </select>
        </label>
        <label>
          Responsável
          <select value={responsavel} onChange={(e) => irPara(situacao, e.target.value)} data-teste="filtro-responsavel-tarefa">
            <option value="">Todos</option>
            {responsaveis.map((r) => (
              <option key={r.id} value={r.id}>
                {r.nome}
              </option>
            ))}
          </select>
        </label>
        <div className="filtros-acoes">
          {pers.controles}
          <button type="button" className="botao-secundario" onClick={() => navegar('/tarefas')}>
            Limpar todos os filtros
          </button>
        </div>
      </div>
      {erro && (
        <p className="aviso aviso-erro" role="alert">
          {erro}
        </p>
      )}
      <p className="resumo-lista" data-teste="filtro-aplicado-tarefas">
        Mostrando: <strong>{ROTULOS_SITUACAO[situacao]}</strong>
        {nomeResponsavel && (
          <>
            {' '}
            · responsável <strong>{nomeResponsavel}</strong>
          </>
        )}{' '}
        — {lista.length} tarefa(s)
      </p>
      {lista.length === 0 ? (
        <Estado tipo="vazio" titulo="nenhuma tarefa com estes filtros" teste="tarefas-vazio" />
      ) : (
        <div className="rolagem">
          <table ref={pers.tabela} className="tabela tabela-compacta" data-teste="lista-tarefas">
            <thead>
              <tr>
                <th>Prioridade</th>
                <th>Tarefa</th>
                <th>Módulo</th>
                <th>Cliente / registro</th>
                <th>Responsável</th>
                <th>Vencimento</th>
                <th className="col-acoes">Ações</th>
              </tr>
            </thead>
            <tbody>
              {lista.map((t) => {
                const s = t.situacao;
                return (
                  <tr key={t.chave} className={s === 'vencidas' ? 'prioridade-critico' : s === 'hoje' ? 'prioridade-urgente' : ''} data-teste={`tarefa-${t.chave}`}>
                    <td className="nowrap">{s === 'vencidas' ? '✖ Vencida' : s === 'hoje' ? '▲ Hoje' : s === 'concluidas' ? '✓ Concluída' : 'ℹ Próxima'}</td>
                    <td>
                      {t.titulo}
                      <div className="texto-secundario">
                        {t.origem}
                        {t.detalhe ? ` · ${t.detalhe}` : ''}
                      </div>
                    </td>
                    <td>{t.modulo}</td>
                    <td>
                      <a href={`#/clientes/${t.cliente.id}`}>{t.cliente.nome}</a>
                      {t.oportunidade && (
                        <div>
                          <a href={`#/oportunidades/${t.oportunidade.id}`}>Oportunidade #{t.oportunidade.numero}</a>
                        </div>
                      )}
                    </td>
                    <td>{t.responsavel.nome}</td>
                    <td className="nowrap">{dataBr(t.data)}</td>
                    <td className="col-acoes">
                      {t.id && !t.concluida && pode('concluir', 'tarefa') && (
                        <button type="button" className="botao-secundario pequeno" onClick={() => void concluir(t.id!)} data-teste={`concluir-${t.chave}`}>
                          Concluir
                        </button>
                      )}
                      {/* P3-12: a próxima ação da Oportunidade também pode ser concluída aqui (nova ação ou sem ação com justificativa) */}
                      {!t.id && t.origem === 'Próxima ação' && t.oportunidade && !t.concluida && pode('editar', 'oportunidade') && (
                        <button type="button" className="botao-secundario pequeno" onClick={() => setConcluirAcao(t)} data-teste={`concluir-${t.chave}`}>
                          Concluir
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      {concluirAcao?.oportunidade && (
        <ModalConcluirProximaAcao
          oportunidade={{ id: concluirAcao.oportunidade.id, proxima_acao: { descricao: concluirAcao.titulo, responsavel: concluirAcao.responsavel } }}
          responsaveis={responsaveis}
          aoFechar={() => setConcluirAcao(null)}
          aoSalvar={() => (setConcluirAcao(null), void carga.recarregar())}
        />
      )}
    </>
  );
}

/** Atividades com um contato (cadastro do contato). */
export function AtividadesDoContato({ contatoId }: { contatoId: string }) {
  const carga = useCarga(() => api.get<{ id: string; data: string; hora: string; texto: string; oportunidade_id: string | null }[]>(`/api/contatos/${contatoId}/atividades`), [contatoId]);
  if (!carga.dados) return <EstadoCarga carregando={carga.carregando} erro={carga.erro} recarregar={carga.recarregar} />;
  return carga.dados.length ? (
    <ul className="linha-tempo" data-teste="atividades-contato">
      {carga.dados.map((a) => (
        <li key={a.id} className="item-linha item-atividade">
          <strong>
            {dataBr(a.data)} {a.hora}
          </strong>{' '}
          {a.oportunidade_id ? <a href={`#/oportunidades/${a.oportunidade_id}`}>{a.texto}</a> : a.texto}
        </li>
      ))}
    </ul>
  ) : (
    <Estado tipo="vazio" titulo="nenhuma atividade com este contato" />
  );
}

/** Referências existentes do Cliente (servidor): só o que existe vira link. */
export interface Referencias {
  oportunidades: Record<string, string>;
  propostas: Record<string, { oportunidadeId: string; propostaId: string }>;
  pedidos: string[];
  ordens: string[];
}

/** Texto de histórico com as referências EXISTENTES clicáveis (PED-, OCF-, OL-, OTI-, Proposta NNNN/AAAA, #Oportunidade) — DEMO-03. */
export function TextoComReferencias({ texto, refs }: { texto: string; refs?: Referencias }) {
  if (!refs) return <>{texto}</>;
  const rota = (ref: string): string | undefined => {
    if (/^PED-\d+$/.test(ref)) return refs.pedidos.includes(ref) ? `/pedidos/${ref}` : undefined;
    if (/^(OCF|OL|OTI)-\d+$/.test(ref)) return refs.ordens.includes(ref) ? `/ordens/${ref}` : undefined;
    if (/^#\d+$/.test(ref)) return refs.oportunidades[ref.slice(1)] ? `/oportunidades/${refs.oportunidades[ref.slice(1)]}` : undefined;
    const p = refs.propostas[ref];
    return p ? `/oportunidades/${p.oportunidadeId}/propostas/${p.propostaId}` : undefined;
  };
  const partes: ReactNode[] = [];
  const re = /(PED-\d{6,8}|OCF-\d{6,8}|OL-\d{6,8}|OTI-\d{6,8}|\b\d{4}\/\d{4}\b|#\d{4,8}\b)/g;
  let ultimo = 0;
  for (const m of texto.matchAll(re)) {
    const r = rota(m[0]);
    if (!r) continue;
    partes.push(texto.slice(ultimo, m.index));
    partes.push(
      <a key={`${m.index}-${m[0]}`} href={`#${r}`} className="ref-timeline">
        {m[0]}
      </a>,
    );
    ultimo = m.index! + m[0].length;
  }
  partes.push(texto.slice(ultimo));
  return <>{partes}</>;
}
